<template>
	<NuxtLink :to="clickIsValid ? routeToProduct : ''" @click="$emit('click')">
		<div class="flex w-full relative flex-col items-start">
			<Favorite
				v-if="product.Id"
				:absolute="true"
				:wrapper-size="8"
				rounded="full"
				:product="product"
			/>
			<div
				:class="`relative ${pictureSize} overflow-hidden aspect-[${pictureAspectRatio}]`"
			>
				<div
					class="flex justify-center h-full w-full"
					:class="isCarousel ? 'items-start' : 'items-center'"
				>
					<NuxtImg
						:src="imageSrc"
						:alt="product?.Name || 'Default product image'"
						class="max-h-full max-w-full w-full object-cover cursor-pointer"
						:loading="lazyLoading ? 'lazy' : 'eager'"
						:preload="!lazyLoading"
						sizes="20vw md:50vw"
						width="364"
						height="546"
						:provider="product?.Files?.[0]?.Path ? undefined : 'storyblok'"
					/>
				</div>
				<ApplicationFlag
					v-for="(flagId, index) in productFlags"
					:key="flagId"
					:data="getFlagData(flagId)"
					:position="getItemPosition(flagId, index)"
				/>
			</div>
			<div class="flex flex-col mt-4 gap-[6px] items-start">
				<span
					v-if="product.Manufacturer?.Name"
					class="font-semibold text-base text-left"
				>
					{{ product.Manufacturer.Name }}
				</span>
				<span class="font-medium text-base text-left text-gray-600">
					{{ product.Name }}
				</span>
				<Price
					:price="product.PriceIncVat!"
					:old-price="oldPrice"
					:size="isCarousel ? 'large' : 'small'"
				/>
			</div>
		</div>
	</NuxtLink>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Price from "~/templates/tendenz/components/Product/ProductAttributes/Price.vue";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { ApplicationFlag } from "~/types/storyblok/storyblok";

interface Props {
	product:
		| Partial<FilteredProductItem & { qty: number }>
		| (ProductItem & { qty: number });
	pictureSize: string;
	pictureAspectRatio?: string;
	clickIsValid: boolean;
	isCarousel: boolean;
	lazyLoading?: boolean;
	routeToProduct: string;
}

const { product } = defineProps<Props>();
defineEmits(["click"]);

const productStore = useProductStore();
const appStore = useAppStore();
const { oldPrice } = useProductPrices(product);

const productFlags = computed(() => {
	if (product?.FlagIdSeed) {
		return product.FlagIdSeed.split(",");
	}

	return [];
});

const { getItemPosition, getFlagData } = useApplicationFlags({
	appFlags: appStore.flags,
	productFlags: productFlags.value
});

const imageSrc = computed(() =>
	product?.Files?.[0]?.Path
		? `${product.Files[0].Path}?mode=max&format=webp&w=390`
		: productStore.defaultProductImage || "images/product/no_image.jpeg"
);
</script>
